import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import AdminLayout from "@/components/layout/AdminLayout";
import NationNav from "@/components/NationNav";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Trophy, Plus, MapPin, Calendar, ChevronRight, Users } from "lucide-react";

const BLANK = { name: "", start_date: "", end_date: "", location: "", venue: "", hotel_info: "", schedule: "", travel_notes: "", team_ids: [] };

export default function AdminTournaments() {
  const [items, setItems] = useState([]);
  const [teams, setTeams] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(BLANK);

  const load = () => api.get("/admin/tournaments").then(({ data }) => setItems(data.items || [])).catch(() => {});
  useEffect(() => { load(); }, []);
  useEffect(() => { api.get("/admin/teams").then(({ data }) => setTeams(data.items || [])).catch(() => {}); }, []);

  const toggleTeam = (id) => setForm((f) => ({ ...f, team_ids: f.team_ids.includes(id) ? f.team_ids.filter((x) => x !== id) : [...f.team_ids, id] }));

  const create = async () => {
    if (!form.name.trim()) { toast.error("Tournament name required"); return; }
    try {
      const payload = Object.fromEntries(Object.entries(form).filter(([, v]) => v !== ""));
      await api.post("/admin/tournaments", payload);
      toast.success("Tournament created");
      setForm(BLANK);
      setShowForm(false);
      load();
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail) || e.message); }
  };

  return (
    <AdminLayout>
      <div className="p-8 max-w-5xl">
        <div className="flex items-center gap-2 label-overline text-[#E63946]"><Trophy className="w-4 h-4" /> CBR Nation</div>
        <div className="flex items-end justify-between">
          <div>
            <h1 data-testid="tournaments-title" className="font-display text-4xl uppercase tracking-tighter mt-2">Tournaments</h1>
            <p className="text-xs text-slate-500 mt-1">Create events, assign teams, and track player availability.</p>
          </div>
          <button data-testid="new-tournament-btn" onClick={() => setShowForm((v) => !v)} className="inline-flex items-center gap-2 bg-[#E63946] text-white px-5 py-2.5 text-sm font-semibold uppercase tracking-wider rounded-sm">
            <Plus className="w-4 h-4" /> New Tournament
          </button>
        </div>

        <NationNav />

        {showForm && (
          <div className="bg-white border border-slate-200 rounded-sm p-5 grid sm:grid-cols-2 gap-3 mb-6">
            <Field label="Event Name" testid="tour-name" value={form.name} onChange={(v) => setForm({ ...form, name: v })} className="sm:col-span-2" placeholder="CBR Nation Summer Classic" />
            <Field label="Start Date" testid="tour-start" type="date" value={form.start_date} onChange={(v) => setForm({ ...form, start_date: v })} />
            <Field label="End Date" testid="tour-end" type="date" value={form.end_date} onChange={(v) => setForm({ ...form, end_date: v })} />
            <Field label="Location" testid="tour-location" value={form.location} onChange={(v) => setForm({ ...form, location: v })} placeholder="Toronto, ON" />
            <Field label="Venue" testid="tour-venue" value={form.venue} onChange={(v) => setForm({ ...form, venue: v })} placeholder="Rogers Centre Fields" />
            <Area label="Hotel Info" testid="tour-hotel" value={form.hotel_info} onChange={(v) => setForm({ ...form, hotel_info: v })} />
            <Area label="Schedule" testid="tour-schedule" value={form.schedule} onChange={(v) => setForm({ ...form, schedule: v })} />
            <Area label="Travel Notes" testid="tour-travel" value={form.travel_notes} onChange={(v) => setForm({ ...form, travel_notes: v })} className="sm:col-span-2" />
            <div className="sm:col-span-2">
              <span className="label-overline text-[10px]">Assign Teams</span>
              <div className="flex flex-wrap gap-2 mt-2">
                {teams.map((t) => (
                  <button key={t.id} data-testid={`assign-team-${t.id}`} onClick={() => toggleTeam(t.id)}
                    className={`px-3 py-1.5 text-xs font-semibold uppercase tracking-wider rounded-sm border ${form.team_ids.includes(t.id) ? "bg-[#0A192F] text-white border-[#0A192F]" : "bg-white border-slate-300 text-slate-600"}`}>
                    {t.name}
                  </button>
                ))}
                {teams.length === 0 && <span className="text-xs text-slate-400">No teams yet — create one first.</span>}
              </div>
            </div>
            <button data-testid="create-tournament-submit" onClick={create} className="sm:col-span-2 self-start bg-[#0A192F] text-white px-5 py-2.5 text-sm font-semibold uppercase tracking-wider rounded-sm">Create Tournament</button>
          </div>
        )}

        <div className="grid sm:grid-cols-2 gap-4">
          {items.map((t) => (
            <Link key={t.id} to={`/admin/tournaments/${t.id}`} data-testid={`tournament-card-${t.id}`} className="bg-white border border-slate-200 hover:border-[#E63946] rounded-sm p-5 transition-colors">
              <div className="flex items-center justify-between">
                <span className="font-display text-2xl uppercase tracking-tight">{t.name}</span>
                <ChevronRight className="w-5 h-5 text-slate-400" />
              </div>
              <div className="flex flex-wrap items-center gap-4 mt-3 text-xs text-slate-500">
                {(t.start_date || t.end_date) && <span className="inline-flex items-center gap-1"><Calendar className="w-3.5 h-3.5" /> {[t.start_date, t.end_date].filter(Boolean).join(" → ")}</span>}
                {t.location && <span className="inline-flex items-center gap-1"><MapPin className="w-3.5 h-3.5" /> {t.location}</span>}
                <span className="inline-flex items-center gap-1"><Users className="w-3.5 h-3.5" /> {(t.teams || []).length} team(s)</span>
              </div>
            </Link>
          ))}
          {items.length === 0 && <div className="sm:col-span-2 text-sm text-slate-500 bg-white border border-slate-200 rounded-sm p-10 text-center">No tournaments yet. Create your first event.</div>}
        </div>
      </div>
    </AdminLayout>
  );
}

function Field({ label, value, onChange, type = "text", placeholder, className = "", testid }) {
  return (
    <label className={`flex flex-col gap-1 ${className}`}>
      <span className="label-overline text-[10px]">{label}</span>
      <input data-testid={testid} type={type} value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} className="border border-slate-300 rounded-sm px-3 py-2 text-sm" />
    </label>
  );
}

function Area({ label, value, onChange, className = "", testid }) {
  return (
    <label className={`flex flex-col gap-1 ${className}`}>
      <span className="label-overline text-[10px]">{label}</span>
      <textarea data-testid={testid} rows="2" value={value} onChange={(e) => onChange(e.target.value)} className="border border-slate-300 rounded-sm px-3 py-2 text-sm" />
    </label>
  );
}
